import React, { useState, useMemo } from "react";
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Checkbox,
    TableSortLabel,
    Box,
    useTheme,
    alpha,
} from "@mui/material";
import { visuallyHidden } from '@mui/utils';
import { Worker } from "../../models/worker";
import WorkerTableRow from "./WorkerTableRow";

interface WorkersTableContentProps {
    workers: Worker[];
    bulkSelectMode: boolean;
    selectedWorkerIds: string[];
    onSelectAll: (checked: boolean) => void;
    onSelectRow: (workerId: string, checked: boolean) => void;
    currentPage: number;
    rowsPerPage: number;
    totalCount: number;
    onPageChange: (page: number) => void;
    onRowsPerPageChange: (rowsPerPage: number) => void;
}

type Order = 'asc' | 'desc';
type OrderBy = 'fullName' | 'nationality' | 'language' | 'professionKey' | 'status' | 'createdAt';

interface HeadCell {
    id: OrderBy | 'recruiter' | 'accountManager' | 'availability' | 'actions';
    label: string;
    numeric: boolean;
    sortable: boolean;
    width?: string | number;
    minWidth?: number;
}

const headCells: HeadCell[] = [
    { id: 'fullName', label: 'Full Name', numeric: false, sortable: true, minWidth: 180 },
    { id: 'status', label: 'Status', numeric: false, sortable: true, width: 130 },
    { id: 'availability', label: 'Availability', numeric: false, sortable: false, width: 120 },
    { id: 'nationality', label: 'Nationality', numeric: false, sortable: true, width: 90 },
    { id: 'language', label: 'Language', numeric: false, sortable: true, minWidth: 100 },
    { id: 'professionKey', label: 'Profession', numeric: false, sortable: true, minWidth: 140 },
    { id: 'recruiter', label: 'Recruiter', numeric: false, sortable: false, minWidth: 140 },
    { id: 'accountManager', label: 'Account Manager', numeric: false, sortable: false, width: 120 },
    { id: 'actions', label: 'Actions', numeric: false, sortable: false, width: 180 },
];

const WorkersTableContent: React.FC<WorkersTableContentProps> = ({
    workers,
    bulkSelectMode,
    selectedWorkerIds,
    onSelectAll,
    onSelectRow,
    currentPage,
    rowsPerPage,
    totalCount,
    onPageChange,
    onRowsPerPageChange,
}) => {
    const theme = useTheme();
    const [order, setOrder] = useState<Order>('asc');
    const [orderBy, setOrderBy] = useState<OrderBy>('fullName');
    
    const handleRequestSort = (property: OrderBy) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };
    
    const isAllSelected = workers.length > 0 && selectedWorkerIds.length === workers.length;
    const isIndeterminate = selectedWorkerIds.length > 0 && selectedWorkerIds.length < workers.length;
    
    // Sort workers
    const sortedWorkers = useMemo(() => {
        return [...workers].sort((a, b) => {
            let aValue: any = '';
            let bValue: any = '';
            
            // Handle special case for language which is now languages array
            if (orderBy === 'language') {
                aValue = (a as any).languages?.[0] || '';
                bValue = (b as any).languages?.[0] || '';
            } else {
                aValue = a[orderBy] || '';
                bValue = b[orderBy] || '';
            }
            
            if (typeof aValue === 'string') aValue = aValue.toLowerCase();
            if (typeof bValue === 'string') bValue = bValue.toLowerCase();
            
            if (order === 'asc') {
                return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
            } else {
                return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
            }
        });
    }, [workers, order, orderBy]);
    
    const handleChangePage = (_event: unknown, newPage: number) => {
        onPageChange(newPage);
    };
    
    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        onRowsPerPageChange(parseInt(event.target.value, 10));
        onPageChange(0);
    };
    
    return (
        <>
            <TableContainer sx={{ flex: 1, position: 'relative' }}>
                <Table
                    stickyHeader
                    aria-label="workers table"
                    sx={{
                        '& .MuiTableCell-root': {
                            borderBottom: `1px solid ${theme.palette.divider}`,
                        },
                        '& .MuiTableHead-root': {
                            '& .MuiTableCell-root': {
                                backgroundColor: theme.palette.mode === 'dark' 
                                    ? theme.palette.background.paper 
                                    : theme.palette.grey[50],
                                fontWeight: 600,
                                color: theme.palette.text.primary,
                                borderBottom: `2px solid ${theme.palette.divider}`,
                                position: 'sticky',
                                top: 0,
                                zIndex: 10,
                            },
                        },
                        '& .MuiTableBody-root': {
                            '& .MuiTableRow-root': {
                                '&:hover': {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.04),
                                },
                                '&:nth-of-type(even)': {
                                    backgroundColor: alpha(theme.palette.grey[500], 0.02),
                                },
                            },
                        },
                    }}
                >
                    <TableHead>
                        <TableRow>
                            {bulkSelectMode && (
                                <TableCell padding="checkbox" sx={{ width: 60 }}>
                                    <Checkbox
                                        color="primary"
                                        indeterminate={isIndeterminate}
                                        checked={isAllSelected}
                                        onChange={(e) => onSelectAll(e.target.checked)}
                                        inputProps={{
                                            'aria-label': 'select all workers',
                                        }}
                                    />
                                </TableCell>
                            )}
                            {headCells.map((headCell) => (
                                <TableCell
                                    key={headCell.id}
                                    align={headCell.numeric ? 'right' : 'left'}
                                    sortDirection={orderBy === headCell.id ? order : false}
                                    sx={{
                                        width: headCell.width,
                                        minWidth: headCell.minWidth,
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {headCell.sortable ? (
                                        <TableSortLabel
                                            active={orderBy === headCell.id}
                                            direction={orderBy === headCell.id ? order : 'asc'}
                                            onClick={() => handleRequestSort(headCell.id as OrderBy)}
                                            sx={{
                                                '&.MuiTableSortLabel-root': {
                                                    color: theme.palette.text.primary,
                                                },
                                                '&.MuiTableSortLabel-root:hover': {
                                                    color: theme.palette.primary.main,
                                                },
                                                '&.Mui-active': {
                                                    color: theme.palette.primary.main,
                                                },
                                            }}
                                        >
                                            {headCell.label}
                                            {orderBy === headCell.id ? (
                                                <Box component="span" sx={visuallyHidden}>
                                                    {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                                </Box>
                                            ) : null}
                                        </TableSortLabel>
                                    ) : (
                                        headCell.label
                                    )}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sortedWorkers.map((worker) => {
                            const isSelected = selectedWorkerIds.includes(worker.workerId);
                            return (
                                <WorkerTableRow
                                    key={worker.workerId}
                                    worker={worker}
                                    bulkSelectMode={bulkSelectMode}
                                    isSelected={isSelected}
                                    onSelectRow={() => onSelectRow(worker.workerId, !isSelected)}
                                />
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            
            <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={currentPage}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                    borderTop: `1px solid ${theme.palette.divider}`,
                    '& .MuiTablePagination-toolbar': {
                        minHeight: 52,
                    },
                }}
            />
        </>
    );
};

export default WorkersTableContent;
